import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Talk from "talkjs";
import i18n from "../i18n";
import { useStableContext } from "../rill/RillReact/share/base";
import { clearAllTemplatesActiveView } from "../utils/all-templates-active-view";
import { openMsbAgent, useMsbAgentSeen } from "../utils/msb-agent";
import {
  getSchedulingAccess,
  getUserAccess,
} from "../utils/organization-helpers";
import { getTalkJsLocale } from "../utils/talkjs-locale";
import {
  calculateFreeTrialDaysLeft,
  isPullActiveCoach,
  isPushActiveCoach,
} from "../utils/user-helpers";
import { getUserToRequest } from "../views/dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import Header from "./header";
import { AthleteFinder } from "./header-elements";
import SvgIcon from "./SvgIcon";
import ThemeToggler from "./ThemeToggler";

const TALK_JS_APP_ID = "tMXnzHXl";

const MobileNav = ({
  expanded,
  searching,
  title,
  navSections,
  toggleMenu,
  isCoach,
  userCanAccessToPush,
  isPushCoachWithEndedTrial,
  handleAthleteSearch,
  handleNavTitle,
  referAsClients,
  handleContractNav,
  activeUser,
  daysLeft,
  NavItem,
  subscriptionsLabel,
  coachingLabel,
  upgradeToPullLabel,
  freeTrialDaysLeftLabel,
  upgradeNowLabel,
  isGym,
  userCanAccessToPerform,
  isPerformPastDue,
}) => {
  return (
    // Show navbar on top if it is a mobile device user
    <aside role="mobile-nav" className={`${expanded ? "expanded" : ""}`}>
      <div role="nav-header">
        {!searching && (
          <div onClick={() => toggleMenu()}>
            <img src="/img/new-icons/menu.svg" alt="menu" />
          </div>
        )}
        {expanded ? (
          <img alt="" />
        ) : searching ? (
          <AthleteFinder
            referAsClients={referAsClients}
            handleShowFinder={handleAthleteSearch}
            mobile
          />
        ) : (
          <div role="nav-header-items">
            <h2>{title}</h2>
            <Header
              mobile
              coachingPortalEnabled={isCoach}
              handleAthleteSearch={handleAthleteSearch}
              handleNavTitle={handleNavTitle}
            />
          </div>
        )}
      </div>
      <div role="nav-links">
        <ul>
          {navSections.all.map((props) => (
            <NavItem
              key={props.item}
              {...props}
              currentTab={title}
              handleNavTitle={handleNavTitle}
              handleContractNav={handleContractNav}
              mobile
            />
          ))}
        </ul>
        {userCanAccessToPush && (
          <>
            <span className="mobile-hidden">{subscriptionsLabel}</span>
            <ul className="mobile-hidden">
              {navSections.subscriptions.map((props) => (
                <NavItem
                  key={props.item}
                  {...props}
                  currentTab={title}
                  handleNavTitle={handleNavTitle}
                  handleContractNav={handleContractNav}
                  mobile
                />
              ))}
            </ul>
          </>
        )}
        {isCoach && (
          <>
            {isPushCoachWithEndedTrial ? (
              <a href="/coach-billing">{upgradeToPullLabel}</a>
            ) : (
              <span>{coachingLabel}</span>
            )}
            <ul>
              {navSections.coaching.map((props) => (
                <NavItem
                  key={props.item}
                  {...props}
                  validateCoach
                  currentTab={title}
                  handleNavTitle={handleNavTitle}
                  handleContractNav={handleContractNav}
                  mobile
                />
              ))}
            </ul>
            <ul className="mobile-hidden">
              {navSections.msbUniversity.map((props) => (
                <NavItem
                  key={props.item}
                  {...props}
                  validateCoach
                  currentTab={title}
                  handleNavTitle={handleNavTitle}
                  handleContractNav={handleContractNav}
                  mobile
                />
              ))}
            </ul>
            <ul>
              {[...navSections.msbAgent, navSections.msbUniversity[1]].map(
                (props) => (
                  <NavItem
                    key={props.item}
                    {...props}
                    validateCoach={!props.onSelect}
                    currentTab={title}
                    handleNavTitle={handleNavTitle}
                    handleContractNav={handleContractNav}
                    mobile
                  />
                )
              )}
            </ul>
            {isGym && (
              <>
                <span>Gym</span>
                <ul>
                  {navSections.gym.map((props) => (
                    <NavItem
                      key={props.item}
                      {...props}
                      currentTab={title}
                      handleNavTitle={handleNavTitle}
                      handleContractNav={handleContractNav}
                      mobile
                    />
                  ))}
                </ul>
              </>
            )}
            {userCanAccessToPerform && (
              <>
                <span>Gym Members</span>
                <ul>
                  {isPerformPastDue && (
                    <NavItem
                      link="/coaching-portal/access-requests?modal[type]=pending-perform-invoice"
                      icon="exclamation-hexagon"
                      item="Pending Payment"
                      currentTab={title}
                      handleNavTitle={handleNavTitle}
                      handleContractNav={handleContractNav}
                      mobile
                      warning
                    />
                  )}
                  {navSections.gymMembers.map((props) => (
                    <NavItem
                      key={props.item}
                      {...props}
                      currentTab={title}
                      handleNavTitle={handleNavTitle}
                      handleContractNav={handleContractNav}
                      mobile
                    />
                  ))}
                </ul>
              </>
            )}
          </>
        )}

        {isCoach && activeUser.coachingFreeTrial && daysLeft > 0 && (
          <div>
            <a href="/coach-billing" role="button" data-button-type="secondary">
              {freeTrialDaysLeftLabel}{" "}
              <span className="clipped font-semibold">{upgradeNowLabel}</span>
            </a>
          </div>
        )}
      </div>
    </aside>
  );
};

function Leftbar({ query = {} }) {
  const { t } = useTranslation("coaching-portal");
  const { locals } = useStableContext();
  const {
    pathname,
    activeUser,
    organizationOwner,
    organizationPermissions: globalOrganizationPermissions = [],
    organizationRoles = [],
  } = locals;
  const hasSeenMsbAgent = useMsbAgentSeen(activeUser);

  const [expanded, setExpanded] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(null);
  const [title, setTitle] = useState("");
  const [searching, setSearching] = useState(false);
  const [unreadChatConversations, setUnreadChatConversations] = useState([]);
  const [
    unreadCommunityChatConversations,
    setUnreadCommunityChatConversations,
  ] = useState([]);
  const chatSessionRef = useRef(null);
  const lastNotificationTimestamp = useRef(null);

  // Replaces handleDashboardPaddings called after setState callbacks
  useEffect(() => {
    const nav = document.querySelector('header[role="main"]');
    if (nav && viewportWidth > 960)
      nav.style.margin = expanded ? "0px 0px 0px 15rem" : "0px 0px 0px 4rem";
  }, [expanded, viewportWidth]);

  function toggleMenu() {
    setExpanded((prev) => !prev);
  }

  function handleContractNav() {
    setExpanded(false);
  }

  function handleNavTitle(newTitle) {
    setTitle(newTitle);
  }

  function handleAthleteSearch() {
    setSearching((prev) => !prev);
  }

  /**
   * Function to display the web notifications
   * @param {*} unreadConversations
   * @param {*} userInSession
   */
  function handleNewMessageNotification(unreadConversations, userInSession) {
    // Find the most recent message by timestamp
    const newMessage = unreadConversations?.reduce((latest, current) => {
      return current.lastMessage.timestamp > latest.lastMessage.timestamp
        ? current
        : latest;
    }, unreadConversations[0]); // Start with the first conversation
    const {
      lastMessage: {
        body,
        isByMe,
        timestamp,
        type,
        conversation: { subject, custom },
        sender: { name: senderName },
      },
    } = newMessage;
    //we are checking if the user is muted in that conversation
    const { muted } = custom;
    const parsedMuted = muted ? JSON.parse(muted) : [];
    const isMuted = parsedMuted.includes(userInSession);
    const currentTime = Date.now(); //actual time in ms
    // Check if the message was sent within the last 2 minutes
    const oneMinutesInMs = 1 * 60 * 1000;
    const isRecentMessage = currentTime - timestamp <= oneMinutesInMs;
    /** The webhook is firing an event if the user sends or reads a message, and we don't want to display a notification if the user
      read a message, so, we are storing the time of the last notifification displayed in the lastNotificationTimestamp state, to compare it
      here with the last message received, if lastNotificationTimestamp doesn't exists, is because it is the first notification displayed,
      and if it exists we are comparing that time with the last message time, if the last message time is bigger that means
      it is a new message event, if not, it is a read message event
      */
    const isNewMessageEvent =
      !lastNotificationTimestamp.current ||
      timestamp > lastNotificationTimestamp.current;
    //Send the notification if the event is "new message" type, the message wasn't send by the user in session, is text, is recent
    //and the user isn't muted from that conversation
    if (
      isNewMessageEvent &&
      !isByMe &&
      type === "text" &&
      isRecentMessage &&
      !isMuted
    ) {
      const notificationBody = subject
        ? `${senderName} sent a new message to the group "${subject}"`
        : senderName;
      if (typeof Notification !== "undefined") {
        new Notification(notificationBody, {
          body: body || "You have a new message",
        });
      }
      lastNotificationTimestamp.current = timestamp;
    }
  }

  /**
   * Update the badge of the message icon only if the messages aren't from community chat
   * @param {*} unreadConversations
   */
  function onChangeUnreadMessages(unreadConversations, userInSession) {
    //Update the unreadChatConversations and unreadCommunityChatConversations state depends on the condition
    // Uses functional setter form to always read current state and avoid stale closures
    const updateUnreadState = (filterCondition, setter) => {
      const filteredMessages =
        unreadConversations?.filter(filterCondition) ?? [];
      setter((prev) => {
        if (
          (filteredMessages.length === 0 && prev.length > 0) ||
          (prev.length === 0 && filteredMessages.length > 0)
        ) {
          return filteredMessages;
        }
        return prev;
      });
    };

    // we are updating the chat state (the messages that don't have isPush / isPress)
    updateUnreadState(
      (conv) =>
        !conv?.conversation?.custom?.isPush &&
        !conv?.conversation?.custom?.isPress,
      setUnreadChatConversations
    );

    // we are updating the community chat state (the messages that have isPush / isPress)
    updateUnreadState(
      (conv) =>
        conv?.conversation?.custom?.isPush ||
        conv?.conversation?.custom?.isPress,
      setUnreadCommunityChatConversations
    );

    //show the web notification everytime there is a new talkjs message
    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "granted" &&
      unreadConversations.length > 0
    ) {
      handleNewMessageNotification(unreadConversations, userInSession);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <expected run on mount>
  useEffect(() => {
    function instanceChat() {
      if (
        process.browser &&
        activeUser.chatEnabled &&
        !chatSessionRef.current
      ) {
        Talk.ready.then(async () => {
          const userChat = await new Talk.User({
            id: activeUser._id,
            name: activeUser.name,
            role: "msbdarktheme",
            locale: getTalkJsLocale(i18n.language),
          });
          const talkJSSession = await new Talk.Session({
            appId: TALK_JS_APP_ID,
            me: userChat,
          });
          if (talkJSSession) {
            chatSessionRef.current = talkJSSession;
            talkJSSession
              .setDesktopNotificationEnabled(false, { alertOnFailure: false })
              .then(() => {
                console.log("TalkJS notifications are disabled");
              })
              .catch((error) => {
                console.error("Error disabling desktop notifications:", error);
              });
            talkJSSession.unreads.onChange((unreadConversations) => {
              onChangeUnreadMessages(
                unreadConversations,
                String(activeUser._id)
              );
            });
            if (
              typeof Notification !== "undefined" &&
              Notification.permission === "default"
            ) {
              Notification.requestPermission()
                .then((permission) => {
                  if (permission === "granted") {
                    console.info(
                      "the desktop notifications permission is enabled"
                    );
                  } else {
                    console.warn(
                      "the desktop notifications permission has been denied"
                    );
                  }
                })
                .catch((error) => {
                  console.error("Error enabling desktop notifications:", error);
                });
            }
          }
        });
      }
    }

    instanceChat();
    setViewportWidth(window.innerWidth);

    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => {
      chatSessionRef.current?.destroy();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const userIsCoach = activeUser.permissions?.includes("coach");
  const userToRequest = userIsCoach
    ? getUserToRequest(activeUser, organizationOwner)
    : activeUser;
  const isCoach = _.get(activeUser, "permissions", []).includes("coach");

  const { referAsClients } = activeUser;

  const { coachingBilling, performBilling } = userToRequest;

  //Constants to detect the user permissions, if the user not belongs to an organization, is able to see the option, and if it's part of an organization
  //We have to check if the user have the permissions
  const {
    userOrganizationCanAccessToWidgets,
    userOrganizationCanAccessToWorkflows,
    userOrganizationCanAccessToExerciseLibrary,
    userCanAccessToPush,
    userOrganizationCanAccessToTemplates,
    userCanAccessToPress,
    isAdminOrOwner,
    isGym,
    userCanAccessToPerform,
    userCanAccessToPull,
  } = getUserAccess(
    activeUser,
    organizationOwner,
    globalOrganizationPermissions,
    organizationRoles
  );
  const isPastDue = coachingBilling?.status === "past_due";
  const isPerformPastDue = performBilling?.status === "past_due";

  const daysLeft = calculateFreeTrialDaysLeft(userToRequest);
  const isPushCoachWithEndedTrial =
    userToRequest.permissions?.includes("coach") &&
    userToRequest.pushAccountInfo &&
    daysLeft < 1 &&
    !isPullActiveCoach(userToRequest);

  const NavItem = ({
    item = "",
    itemKey: itemKeyProp,
    link = "#",
    trigger = "",
    icon,
    iconSrc,
    validateCoach = false,
    validateOnlyPushCoach = false,
    validateIsPush = false,
    comingSoon = false,
    handleNavTitle = () => {},
    handleContractNav = () => {},
    currentTab = "",
    mobile = false,
    warning,
    isNew = false,
    onSelect,
    iconNoFilter = false,
  }) => {
    const itemKey = itemKeyProp == null ? item : itemKeyProp;
    const valid = validateCoach
      ? isPushActiveCoach(userToRequest)
      : validateOnlyPushCoach
        ? isPushCoachWithEndedTrial || isPushActiveCoach(userToRequest)
        : validateIsPush
          ? userCanAccessToPush
          : true;
    const find =
      typeof trigger !== "string" &&
      !!trigger.find((link) => _.startsWith(pathname, `${link}`));
    // `trigger` defaults to "", and lodash's startsWith treats "" as a prefix
    // of every string, so guard on a truthy trigger or every item without one
    // (e.g. the perform "Pending Payment" warning) would match every page.
    const matchesCurrentPath =
      find || (!!trigger && _.startsWith(pathname, `${trigger}`));

    // biome-ignore lint/correctness/useExhaustiveDependencies: <expected run on mount>
    useEffect(() => {
      if (mobile)
        matchesCurrentPath
          ? currentTab !== item && handleNavTitle(item)
          : _.startsWith(pathname, `/coaching-portal/settings`) &&
            currentTab !== t("leftbar.settings") &&
            handleNavTitle(t("leftbar.settings"));
    }, []);
    return (
      <li
        onClick={handleContractNav}
        aria-current={matchesCurrentPath ? true : undefined}
        {...([
          "Subscriber list",
          "Client overview",
          "Athlete overview",
          "Task list",
          "Income",
          "Store",
          "Plan manager",
          "Scheduling",
          "MSB University",
        ].includes(itemKey) && { className: "mobile-hidden" })} // hide these options on mobile cause they are not yet responsive
        data-disabled={!valid}
        data-warning={warning}
        data-new={isNew || undefined}
      >
        <a
          href={valid && !comingSoon ? link : "#"}
          onClick={(e) => {
            if (!valid || onSelect) e.preventDefault();
            if (valid && onSelect) onSelect(e.currentTarget);
            // Clear any sub-view the coach left open inside Templates (e.g. a
            // program template) so navigating here via the leftbar always
            // lands on the Templates grid, not back on that sub-view - see
            // `all-templates-active-view.js` for why that state can outlive
            // the page it was set on.
            if (itemKey === "Templates" && valid) clearAllTemplatesActiveView();
          }}
          aria-disabled={!valid}
          className="rounded-lg"
        >
          {icon !== undefined || iconSrc ? (
            <div>
              <SvgIcon
                src={iconSrc || `/img/new-icons/${icon}.svg`}
                noFilterOnLight={
                  matchesCurrentPath || iconNoFilter ? true : undefined
                }
              />
            </div>
          ) : null}
          <span>{item}</span>
          {isNew && (
            <span
              className="nav-new-badge"
              style={{
                marginLeft: "-0.5rem",
                backgroundColor: "var(--danger)",
                color: "#fff",
                borderRadius: "var(--border-radius-md)",
                padding: "2px 8px",
                fontSize: "0.7em",
                fontWeight: 700,
                letterSpacing: "0.02em",
              }}
            >
              {t("leftbar.newBadge")}
            </span>
          )}
        </a>
        {/* init of badge on chat */}
        {itemKey === "Chat" && renderBadge(itemKey, unreadChatConversations)}
        {itemKey === "Community Chat" &&
          renderBadge(itemKey, unreadCommunityChatConversations)}
        {/* end of badge on chat */}
      </li>
    );
  };

  const navSections = {
    all: [
      {
        itemKey: "Exercise library",
        item: t("leftbar.exerciseLibrary"),
        link: "/coaching-portal/exercise-library",
        trigger: "/coaching-portal/exercise-library",
        icon: "layer-group",
        iconLight: "layer-group-light",
      },
      {
        itemKey: "Templates",
        item: t("leftbar.templates"),
        link: "/coaching-portal/all-templates?owner=coach",
        trigger: [
          "/coaching-portal/all-templates",
          "/coaching-portal/templates",
        ],
        icon: "dumbbell",
        iconLight: "dumbbell-light",
      },
      {
        itemKey: "Widgets",
        item: t("leftbar.widgets"),
        link: "/coaching-portal/widgets",
        trigger: "/coaching-portal/widgets",
        icon: "widgets",
        iconLight: "widgets-light",
      },
      {
        itemKey: "Scheduling",
        item: "Scheduling",
        link: "/coaching-portal/scheduling",
        trigger: "/coaching-portal/scheduling",
        iconSrc: "/img/favicon/calendar-plus.png",
      },
    ],
    subscriptions: [
      {
        itemKey: "Store",
        item: t("leftbar.store"),
        link: "/coaching-portal/store",
        trigger: "/coaching-portal/store",
        icon: "shopping-bag",
        iconLight: "shopping-bag-light",
      },
      {
        itemKey: "Subscriber list",
        item: t("leftbar.subscriberList"),
        link: "/coaching-portal/subscribers",
        trigger: "/coaching-portal/subscribers",
        icon: "list-ul",
        iconLight: "list-ul-light",
        comingSoon: false,
      },
    ],
    coaching: [
      {
        itemKey: referAsClients ? "Client overview" : "Athlete overview",
        item: referAsClients
          ? t("leftbar.clientOverview")
          : t("leftbar.athleteOverview"),
        link: "/coaching-portal/coaching-dashboard?metric=workouts",
        trigger: "/coaching-portal/coaching-dashboard",
        icon: "clipboard-notes",
        iconLight: "clipboard-notes-light",
      },
      {
        itemKey: referAsClients ? "Client list" : "Athlete list",
        item: referAsClients
          ? t("leftbar.clientList")
          : t("leftbar.athleteList"),
        link: `/coaching-portal/athletes?${encodeURIComponent(
          "filters[status.inactive]"
        )}=${encodeURIComponent("false")}&filterGroup=all${
          isPastDue ? "&modal[type]=pending-invoice" : ""
        }`,
        trigger: ["/coaching-portal/athletes", "/athlete-portal/"],
        icon: "user-circle",
        iconLight: "user-circle-light",
      },
      {
        itemKey: "Task list",
        item: t("leftbar.taskList"),
        link: "/coaching-portal/tasks?tab=programming",
        trigger: "/coaching-portal/tasks",
        icon: "check-circle",
        iconLight: "check-circle-light",
      },
      {
        itemKey: "Chat",
        item: t("leftbar.chat"),
        link: "/coaching-portal/chat",
        trigger: "/coaching-portal/chat",
        icon: "chat",
        iconLight: "chat-light",
      },
    ],
    gym: [
      // PRESS
      {
        itemKey: "Membership list",
        item: t("leftbar.membershipList"),
        link: "/coaching-portal/memberships",
        trigger: "/coaching-portal/memberships",
        icon: "list-ul",
        iconStyle: {
          height: "12px",
        },
      },
    ],
    gymMembers: [
      // PERFORM
      {
        item: t("leftbar.accessRequests"),
        link: `/coaching-portal/access-requests${isPerformPastDue ? "?modal[type]=pending-perform-invoice" : ""}`,
        trigger: "/coaching-portal/access-requests",
        icon: "access-key",
        iconStyle: {
          height: "12px",
        },
      },
      {
        item: t("leftbar.membersList"),
        link: "/coaching-portal/gym-members",
        trigger: "/coaching-portal/gym-members",
        icon: "list-ul",
        iconStyle: {
          height: "12px",
        },
      },
    ],
    msbAgent: [
      {
        itemKey: "MSB Agent",
        item: t("leftbar.msbAgent"),
        icon: "magic-star",
        iconNoFilter: true,
        isNew: !hasSeenMsbAgent,
        onSelect: (trigger) => openMsbAgent(trigger, activeUser),
      },
    ],
    msbUniversity: [
      {
        itemKey: "MSB University",
        item: t("leftbar.msbUniversity"),
        link: "/coaching-portal/msb-university",
        trigger: "/coaching-portal/msb-university",
        icon: "university",
        iconLight: "university-light",
      },
      {
        itemKey: "MSB Services",
        item: t("leftbar.msbServices"),
        link: "/coaching-portal/services",
        trigger: "/coaching-portal/services",
        icon: "shop",
      },
    ],
  };

  if (userCanAccessToPush && isAdminOrOwner) {
    navSections.all.push({
      itemKey: "Plan manager",
      item: t("leftbar.planManager"),
      link: "/coaching-portal/plan-manager",
      trigger: "/coaching-portal/plan-manager",
      icon: "folder-open",
    });
    // Every PRESS gym is also a PUSH coach (create-membership-product.js
    // asserts pushAccountInfo), so this single condition covers both.
    navSections.all.push({
      itemKey: "Income",
      item: t("leftbar.income"),
      link: "/coaching-portal/income",
      trigger: "/coaching-portal/income",
      icon: "income",
      iconLight: "income-light",
      isNew: true,
    });
  }
  if (isAdminOrOwner || userOrganizationCanAccessToWorkflows) {
    navSections.all.push({
      itemKey: "Custom workflows",
      item: t("leftbar.customWorkflows"),
      link: "/coaching-portal/custom-workflows",
      trigger: "/coaching-portal/custom-workflows",
      icon: "custom-workflows",
    });
  }
  if (
    userCanAccessToPush ||
    userCanAccessToPress ||
    userToRequest?.pullAccountInfo?.inAppCommunityEnabled
  ) {
    navSections.all.push({
      itemKey: "Community Chat",
      item: t("leftbar.communityChat"),
      link: "/coaching-portal/community-chat",
      trigger: "/coaching-portal/community-chat",
      icon: "community-chat",
      iconLight: "community-chat-light",
    });
  }
  //hide or show the options if user has access/permissions to the functionalities
  if (!userOrganizationCanAccessToWidgets) {
    const filteredAll = navSections.all.filter(
      ({ itemKey }) => itemKey !== "Widgets"
    );
    navSections.all = filteredAll;
  }
  if (!userOrganizationCanAccessToExerciseLibrary) {
    const filteredAll = navSections.all.filter(
      ({ itemKey }) => itemKey !== "Exercise library"
    );
    navSections.all = filteredAll;
  }

  if (!userOrganizationCanAccessToTemplates) {
    const filteredAll = navSections.all.filter(
      ({ itemKey }) => itemKey !== "Templates"
    );
    navSections.all = filteredAll;
  }

  // Scheduling hidden by default. Show only when the user's tier has it enabled.
  const { schedulingEnabled } = getSchedulingAccess(userToRequest, {
    userCanAccessToPress,
    userCanAccessToPerform,
    userCanAccessToPull,
  });
  if (!schedulingEnabled) {
    navSections.all = navSections.all.filter(
      ({ itemKey }) => itemKey !== "Scheduling"
    );
  }

  if (userToRequest.leaderboard) {
    navSections.all.push({
      itemKey: "Leaderboard",
      item: t("leftbar.leaderboard"),
      link: "/coaching-portal/challenge-report",
      trigger: "/coaching-portal/challenge-report",
      icon: "stats-chart-outline",
      iconLight: "stats-chart-outline",
    });
  }

  // Show navbar on left if it is a desktop browser user
  const DesktopNav = () => {
    const isInSubscribersListSideDrover = !!(
      (pathname.includes("coaching-portal/subscribers") ||
        pathname.includes("coaching-portal/memberships")) &&
      query.customer
    );

    return (
      <aside
        role="main"
        open={expanded ? true : undefined}
        className={isInSubscribersListSideDrover ? "hide-leftbar" : ""}
      >
        <SvgIcon
          src={"/img/new-icons/arrow-square-right.svg"}
          onClickFn={() => toggleMenu()}
          classNames={expanded ? "expanded" : ""}
          id="handle-menu"
        />
        <div>
          <div className="msb-logo"></div>
        </div>
        <div className="nav-content">
          <ul>
            {navSections.all.map((props) => (
              <NavItem key={props.item} {...props} />
            ))}
          </ul>
          {userCanAccessToPush && (
            <ul>
              <div style={{ opacity: expanded ? "0.7" : 0 }}>
                {t("leftbar.subscriptions")}
              </div>
              {navSections.subscriptions.map((props) => (
                <NavItem key={props.item} {...props} />
              ))}
            </ul>
          )}
          {isCoach && (
            <>
              <ul>
                {!isPushCoachWithEndedTrial && (
                  <div style={{ opacity: expanded ? "0.7" : 0 }}>
                    {t("leftbar.coaching")}
                  </div>
                )}
                {isPushCoachWithEndedTrial ? (
                  <a
                    role="button"
                    data-button-type="outline"
                    href="/coach-billing"
                  >
                    {t("leftbar.upgradeToPull")}
                  </a>
                ) : null}
                {isPastDue ? (
                  <NavItem
                    link="/coaching-portal/athletes?modal[type]=pending-invoice"
                    icon="exclamation-hexagon"
                    itemKey="Pending Payment"
                    item={t("leftbar.pendingPayment")}
                    warning
                  />
                ) : null}
                {navSections.coaching.map((props) => (
                  <NavItem key={props.item} {...props} validateCoach />
                ))}
              </ul>

              {isGym && (
                <ul className="Nav-List">
                  <div style={{ opacity: expanded ? "0.7" : 0 }}>
                    {t("leftbar.gym")}
                  </div>
                  {navSections.gym.map((props) => (
                    <NavItem key={props.item} {...props} />
                  ))}
                </ul>
              )}

              {userCanAccessToPerform && (
                <ul className="Nav-List">
                  <div style={{ opacity: expanded ? "0.7" : 0 }}>
                    Gym Members
                  </div>
                  {isPerformPastDue ? (
                    <NavItem
                      link="/coaching-portal/access-requests?modal[type]=pending-perform-invoice"
                      icon="exclamation-hexagon"
                      item="Pending Payment"
                      warning
                    />
                  ) : null}
                  {navSections.gymMembers.map((props) => (
                    <NavItem key={props.item} {...props} />
                  ))}
                </ul>
              )}
              <ul
                className="Nav-List"
                style={{
                  borderBottom: "none",
                }}
              >
                {[
                  navSections.msbUniversity[0],
                  ...(isCoach ? navSections.msbAgent : []),
                  ...navSections.msbUniversity.slice(1),
                ].map((props) => (
                  <NavItem
                    key={props.item}
                    {...props}
                    validateOnlyPushCoach={!props.onSelect}
                  />
                ))}
              </ul>
              {!userCanAccessToPush && isAdminOrOwner ? (
                <ul className="Nav-List request-push-access">
                  <div>{t("leftbar.sellSubscriptionsWithPush")}</div>
                  <li>
                    <a
                      href="https://form.typeform.com/to/jOPku6ra"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-mode="popup"
                      role="button"
                      data-button-type="outline"
                    >
                      <span>{t("leftbar.requestAccessToPush")}</span>
                    </a>
                  </li>
                  {navSections.subscriptions.map((props) => (
                    <NavItem key={props.item} {...props} validateIsPush />
                  ))}
                </ul>
              ) : null}
            </>
          )}
        </div>
        <footer>
          <div>
            <a
              role="button"
              data-button-type="secondary"
              href="https://form.typeform.com/to/Dl4pAwN2"
              target="_blank"
              rel="noopener"
            >
              <SvgIcon src={`/img/new-icons/Bug.svg`} />
              <span>{t("leftbar.reportBug")}</span>
            </a>
          </div>
          <div className="theme-container">
            <ThemeToggler expanded={expanded} />
          </div>
        </footer>
      </aside>
    );
  };

  return (
    <>
      <MobileNav
        expanded={expanded}
        searching={searching}
        title={title}
        toggleMenu={toggleMenu}
        navSections={navSections}
        isCoach={isCoach}
        userCanAccessToPush={userCanAccessToPush}
        isPushCoachWithEndedTrial={isPushCoachWithEndedTrial}
        handleAthleteSearch={handleAthleteSearch}
        handleNavTitle={handleNavTitle}
        referAsClients={referAsClients}
        handleContractNav={handleContractNav}
        activeUser={activeUser}
        daysLeft={daysLeft}
        NavItem={NavItem}
        isGym={isGym}
        userCanAccessToPerform={userCanAccessToPerform}
        isPerformPastDue={isPerformPastDue}
        subscriptionsLabel={t("leftbar.subscriptions")}
        coachingLabel={t("leftbar.coaching")}
        upgradeToPullLabel={t("leftbar.upgradeToPull")}
        freeTrialDaysLeftLabel={t("leftbar.freeTrialDaysLeft", {
          daysLeft,
        })}
        upgradeNowLabel={t("common:upgradeNow")}
      />
      <DesktopNav />
    </>
  );
}

export default React.memo(Leftbar, (prevProps, nextProps) => {
  return JSON.stringify(prevProps.query) === JSON.stringify(nextProps.query);
});

const renderBadge = (item, unreadConversations) => {
  return (
    unreadConversations.length > 0 && (
      <div className="badge-unread-notification"></div>
    )
  );
};
